import * as THREE from 'three'
import { SHOT_SIZE, SHOT_VIEWS, perspShot, orthoShot, screenshotEntry } from './shotSpec.js'

// ============================================================================
//  courtShots — generuje zrzuty boiska (PNG, stały rozmiar 16:9) do zapytania
//  o wycenę. Renderuje CZTERY widoki z shotSpec własnymi, tymczasowymi kamerami
//  (kamera/obrót użytkownika zostają nietknięte) w buforze renderera
//  przestawionym na SHOT_SIZE — rozmiar CSS kanwy się nie zmienia, a cała
//  sekwencja jest synchroniczna (bez await między klatkami), więc użytkownik
//  nie widzi przebłysku. Na końcu przywraca bufor, widok, piłkę i oświetlenie.
//
//  Kanwa WebGL jest przezroczysta (tło rysuje CSS), więc każdą klatkę składamy
//  na offscreen canvas 2D z jednolitym tłem w tonie strony i dopiero ten
//  kodujemy do PNG. Zrzuty zawsze w trybie dziennym (wierne kolory
//  nawierzchni dla handlowca), niezależnie od przełącznika dzień/noc.
// ============================================================================

// Płaskie tło w tonie strony (styles.css: body, środkowy stop gradientu).
// Celowo BEZ gradientu: gładki gradient na całej klatce rozbijał kompresję PNG
// (~1 MB na zrzut); jednolite tło daje kilkukrotnie mniejsze pliki.
const BG_COLOR = '#f1f3f6'
function paintBackground(ctx, w, h) {
  ctx.fillStyle = BG_COLOR
  ctx.fillRect(0, 0, w, h)
}

// Narożniki bryły kosza (Box3 modelu w świecie) — dokładają się do kadru, by
// tablica/słup nie były ucięte przy małej płycie. Brak kosza → pusta lista.
function hoopPoints(hoopGroup) {
  if (!hoopGroup) return []
  const box = new THREE.Box3().setFromObject(hoopGroup, true)
  if (box.isEmpty()) return []
  const pts = []
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z]) pts.push([x, y, z])
  return pts
}

// Kamera perspektywiczna wg shotSpec.perspShot (near/far jak Stage.persp).
function perspCamera(bounds, spec, aspect, extraPoints) {
  const p = perspShot(bounds, { dir: spec.dir, aspect, extraPoints })
  const cam = new THREE.PerspectiveCamera(p.fov, aspect, 0.3, 80)
  cam.position.set(...p.position)
  cam.lookAt(...p.target)
  return cam
}

// Kamera ortograficzna wg shotSpec.orthoShot (z góry, +Z w dół ekranu jak Stage.ortho).
function orthoCamera(bounds, aspect) {
  const o = orthoShot(bounds, { aspect })
  const cam = new THREE.OrthographicCamera(o.left, o.right, o.top, o.bottom, 0.1, 300)
  cam.position.set(...o.position)
  cam.up.set(...o.up)
  cam.lookAt(...o.target)
  return cam
}

// Zwraca tablicę wpisów `screenshots` (shotSpec.screenshotEntry) w kolejności
// SHOT_VIEWS. Rzuca przy błędzie renderu/kodowania — wywołujący (modal wyceny)
// łapie to przez collectScreenshots i wysyła wycenę bez zrzutów.
export async function captureCourtShots({ stage, court, ball, lighting, getHoopGroup }) {
  const renderer = stage.renderer
  const { width, height } = SHOT_SIZE
  const aspect = width / height

  // Chwila na odmalowanie UI (przycisk „Wysyłanie…"), zanim zablokujemy wątek
  // czterema renderami i kodowaniem PNG. Celowo setTimeout, NIE
  // requestAnimationFrame: w ukrytej karcie rAF nie odpala wcale i wysyłka
  // wisiałaby w nieskończoność.
  await new Promise((resolve) => setTimeout(resolve, 32))

  const prev = {
    size: renderer.getSize(new THREE.Vector2()),
    pixelRatio: renderer.getPixelRatio(),
    view: stage.view,
    ballVisible: ball.mesh.visible,
    mix: lighting.mix,
    target: lighting.target,
    exposure: renderer.toneMappingExposure,
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Brak kontekstu 2D do złożenia zrzutu')

  const out = []
  const target = new THREE.Vector3()
  const extraPoints = hoopPoints(getHoopGroup?.() ?? null)
  try {
    renderer.setDrawingBufferSize(width, height, 1)
    lighting.forceMix(0) // zawsze dzień
    renderer.toneMappingExposure = lighting.exposure

    for (const spec of SHOT_VIEWS) {
      const is2d = spec.kind === '2d'
      court.setView(is2d ? '2d' : '3d') // 2D: kratka, wymiary, płaskie kolory, bez cieni
      ball.mesh.visible = !is2d
      const camera = is2d ? orthoCamera(court.bounds, aspect) : perspCamera(court.bounds, spec, aspect, extraPoints)
      // Siła faktury kafli wg dystansu kamery zrzutu (w 2D Court i tak trzyma 0).
      target.set(0, 0.4, court.bounds.centerZ)
      court.setTextureStrength(camera.position.distanceTo(target))

      renderer.render(stage.scene, camera)
      paintBackground(ctx, width, height)
      ctx.drawImage(renderer.domElement, 0, 0, width, height)
      out.push(screenshotEntry(spec, canvas.toDataURL('image/png')))
    }
  } finally {
    court.setView(prev.view)
    ball.mesh.visible = prev.ballVisible
    lighting.forceMix(prev.mix)
    lighting.target = prev.target // trwające przejście dzień↔noc dokończy się normalnie
    renderer.toneMappingExposure = prev.exposure
    renderer.setPixelRatio(prev.pixelRatio)
    renderer.setSize(prev.size.x, prev.size.y, false)
    stage.render() // świeża klatka użytkownika w buforze ekranu, bez czekania na pętlę
  }
  return out
}
